import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import drIsInLogo from "../../assets/dr-is-in.png";
import "./ParentTeacherLogin.css";

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "https://doctorisin-genesis-1-0.onrender.com").replace(/\/+$/, "");
const raw = axios.create({ baseURL: API_BASE, timeout: 15000 });

const PT_PORTAL_ROUTE = "/parent-teacher-portal";
const PT_PORTAL_RETURN_KEY = "pt_portal_return";

function normalizeBearer(t: string) {
  return (t || "").replace(/^Bearer\s+/i, "").trim();
}

function clean(v: string) {
  return (v || "").trim();
}

function normOrgCode(v: string) {
  return clean(v).replace(/[^A-Za-z0-9]/g, "").toUpperCase();
}

function digitsOnly(v: string) {
  return (v || "").replace(/[^\d]/g, "");
}

function setBoth(k: string, v: string) {
  try {
    sessionStorage.setItem(k, v);
  } catch {}
  try {
    localStorage.setItem(k, v);
  } catch {}
}

function removeBoth(k: string) {
  try {
    sessionStorage.removeItem(k);
  } catch {}
  try {
    localStorage.removeItem(k);
  } catch {}
}

function clearNonPtAuth() {
  [
    "admin_token",
    "admin_access_token",
    "doctor_token",
    "doctor_access_token",
    "doctor_id",
    "student_token",
    "student_access_token",
    "student_id",
    "student_pk",
    "access_token",
    "token",
    "role",
  ].forEach(removeBoth);
}

function clearPtAuth() {
  [
    "pt_access_token",
    "pt_token",
    "pt_pk",
    "pt_code",
    "pt_role",
    "pt_index",
    "org_code",
    "parent_access_token",
    "parent_token",
    "teacher_access_token",
    "teacher_token",
    "display_name",
  ].forEach(removeBoth);
}

function parsePtCode(input: string): { ok: boolean; code: string; role: "parent" | "teacher"; index: number } {
  const raw = clean(input).toUpperCase().replace(/\s+/g, "");
  if (!raw) return { ok: false, code: "", role: "parent", index: 0 };

  const m = raw.match(/^([PT])(\d{1,3})$/);
  if (!m) return { ok: false, code: raw, role: "parent", index: 0 };

  const letter = m[1];
  const idx = Number(m[2]);
  if (!Number.isFinite(idx) || idx <= 0) return { ok: false, code: raw, role: "parent", index: 0 };

  const role = letter === "P" ? "parent" : "teacher";
  const code = `${letter}${String(idx).padStart(2, "0")}`;
  return { ok: true, code, role, index: idx };
}

function parseFlexiblePtCode(
  input: string
): { ok: boolean; code: string; role: "parent" | "teacher"; index: number } {
  const raw = clean(input).toUpperCase().replace(/\s+/g, "");
  if (!raw) return { ok: false, code: "", role: "parent", index: 0 };

  const shortParsed = parsePtCode(raw);
  if (shortParsed.ok) return shortParsed;

  const parentGenerated = raw.match(/^PAR0*([0-9]{1,3})(?:_[A-Z0-9_]+)?$/);
  if (parentGenerated) {
    const idx = Number(parentGenerated[1] || 0);
    return {
      ok: true,
      code: raw,
      role: "parent",
      index: Number.isFinite(idx) ? idx : 0,
    };
  }

  const teacherGenerated = raw.match(/^TCH0*([0-9]{1,3})(?:_[A-Z0-9_]+)?$/);
  if (teacherGenerated) {
    const idx = Number(teacherGenerated[1] || 0);
    return {
      ok: true,
      code: raw,
      role: "teacher",
      index: Number.isFinite(idx) ? idx : 0,
    };
  }

  if (/^[A-Z0-9_]+$/.test(raw)) {
    return { ok: true, code: raw, role: "parent", index: 0 };
  }

  return { ok: false, code: raw, role: "parent", index: 0 };
}

function normalizePtLoginCode(input: string): string {
  const parsed = parseFlexiblePtCode(input);
  return parsed.ok ? parsed.code : clean(input).toUpperCase().replace(/\s+/g, "");
}

function normalizeServerRole(v: any): string {
  const raw = String(v || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw === "pt" || raw === "parent_teacher" || raw === "parentteacher") return "pt";
  if (raw === "parent") return "parent";
  if (raw === "teacher") return "teacher";
  return raw;
}

function inferAcceptedRole(data: any, fallbackPtRole: "parent" | "teacher"): "pt" | "parent" | "teacher" {
  const apiRole = normalizeServerRole(data?.role);
  const apiPtRole = normalizeServerRole(data?.pt_role);

  if (apiRole === "parent" || apiPtRole === "parent") return "parent";
  if (apiRole === "teacher" || apiPtRole === "teacher") return "teacher";
  if (apiRole === "pt") return "pt";

  return fallbackPtRole;
}

function extractPtPk(data: any): string {
  const candidates = [
    data?.pt_pk,
    data?.pt_id,
    data?.id,
    data?.user_id,
    data?.parent_teacher_id,
    data?.parent_teacher_pk,
    data?.account_id,
    data?.sub,
    data?.user?.pt_pk,
    data?.user?.pt_id,
    data?.user?.id,
  ];

  for (const value of candidates) {
    const s = String(value ?? "").trim();
    if (s) return s;
  }

  return "";
}

function toErrorText(detail: any): string {
  if (!detail) return "Parent/Teacher login failed.";

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail
      .map((item: any) => {
        const where = Array.isArray(item?.loc) ? item.loc.join(" > ") : "";
        const msg = typeof item?.msg === "string" ? item.msg : "Validation error";
        return where ? `${where}: ${msg}` : msg;
      })
      .join(" | ");
  }

  if (typeof detail === "object") {
    if (typeof detail?.detail === "string") return detail.detail;
    if (typeof detail?.message === "string") return detail.message;
    try {
      return JSON.stringify(detail);
    } catch {
      return "Parent/Teacher login failed.";
    }
  }

  return String(detail);
}

async function tryPtLogin(payload: any) {
  const paths = [
    "/auth/login_pt",
    "/auth/login_parent_teacher",
    "/auth/login_parentteacher",
    "/pt/login",
    "/parent-teacher/login",
    "/parent_teacher/login",
  ];

  let lastErr: any = null;

  for (const p of paths) {
    try {
      const res = await raw.post(p, payload, {
        headers: { "Content-Type": "application/json" },
      });
      return res;
    } catch (e: any) {
      lastErr = e;
    }
  }

  throw lastErr || new Error("Parent/Teacher login failed.");
}

const ParentTeacherLogin: React.FC = () => {
  const navigate = useNavigate();
  const year = new Date().getFullYear();

  const [orgCode, setOrgCode] = useState("");
  const [ptCodeInput, setPtCodeInput] = useState("");
  const [pin, setPin] = useState("");

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    clearNonPtAuth();
    clearPtAuth();

    ["portal_return", "return_to", "post_login_redirect", "after_login"].forEach(removeBoth);

    try {
      sessionStorage.setItem(PT_PORTAL_RETURN_KEY, PT_PORTAL_ROUTE);
    } catch {}
  }, []);

  const parsed = useMemo(() => parseFlexiblePtCode(ptCodeInput), [ptCodeInput]);

  const canSubmit = useMemo(() => {
    return normOrgCode(orgCode).length > 0 && parsed.ok && digitsOnly(pin).length === 6;
  }, [orgCode, parsed.ok, pin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr("");
    setLoading(true);

    try {
      clearNonPtAuth();
      clearPtAuth();

      const org_code = normOrgCode(orgCode);
      const pt = parseFlexiblePtCode(ptCodeInput);
      const pinClean = digitsOnly(pin).slice(0, 6);

      if (!pt.ok) {
        throw new Error("Enter a valid Parent/Teacher ID.");
      }

      if (pinClean.length !== 6) {
        throw new Error("Enter a valid 6-digit PIN.");
      }

      const payload = {
        org_code,
        pt_code: pt.code,
        pt_index: pt.index,
        pt_role: pt.role,
        pin: pinClean,
      };

      const res = await tryPtLogin(payload);
      const data = res?.data ?? {};

      const tokenRaw = String(data?.access_token || data?.token || "");
      if (!tokenRaw) {
        throw new Error("Parent/Teacher login failed (missing token).");
      }

      const token = normalizeBearer(tokenRaw);
      const acceptedRole = inferAcceptedRole(data, pt.role);
      const effectivePtRole = acceptedRole === "pt" ? pt.role : acceptedRole;
      const storedPtPk = extractPtPk(data);

      setBoth("role", acceptedRole === "pt" ? "pt" : effectivePtRole);

      setBoth("pt_access_token", token);
      setBoth("pt_token", token);

      if (effectivePtRole === "parent") {
        setBoth("parent_access_token", token);
        setBoth("parent_token", token);
      }

      if (effectivePtRole === "teacher") {
        setBoth("teacher_access_token", token);
        setBoth("teacher_token", token);
      }

      setBoth("org_code", String(data?.org_code || payload.org_code));
      setBoth("pt_code", String(data?.pt_code || payload.pt_code));
      setBoth("pt_role", String(data?.pt_role || effectivePtRole));
      setBoth("pt_index", String(data?.pt_index ?? payload.pt_index));

      if (storedPtPk) {
        setBoth("pt_pk", storedPtPk);
      }

      if (data?.display_name != null) {
        setBoth("display_name", String(data.display_name));
      } else {
        setBoth("display_name", String(data?.pt_code || payload.pt_code));
      }

      try {
        console.log("PT LOGIN RESPONSE:", data);
        console.log("Stored pt_pk:", storedPtPk);
      } catch {}

      const destination = effectivePtRole === "teacher" ? "/teacher-portal" : "/parent-portal";

      try {
        sessionStorage.setItem(PT_PORTAL_RETURN_KEY, destination);
      } catch {}

      navigate(destination, { replace: true });
    } catch (e: any) {
      const detail = e?.response?.data?.detail ?? e?.response?.data ?? e?.message;
      setErr(toErrorText(detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-login-page">
      <div className="pt-login-card">
        <div className="pt-login-brand">
          <img className="pt-login-logo" src={drIsInLogo} alt="Doctor Is In" />
          <div className="pt-login-brandtext">
            <div className="pt-login-title">Parent & Teacher Access</div>
            <div className="pt-login-tagline">AI Wellness Advisor with a human touch.</div>
            <div className="pt-login-subtitle">Parents and teachers use the same secure sign-in. You will be routed to the correct portal automatically.</div>
          </div>
        </div>

        {err ? <div className="pt-login-error">{String(err)}</div> : null}

        <form onSubmit={handleSubmit}>
          <label className="pt-login-label">Org Code</label>
          <input
            className="pt-login-input"
            value={orgCode}
            onChange={(e) => setOrgCode(e.target.value)}
            onBlur={() => setOrgCode(normOrgCode(orgCode))}
            placeholder="e.g. SMHSUNION"
            autoComplete="off"
          />

          <label className="pt-login-label">Parent/Teacher ID</label>
          <input
            className="pt-login-input"
            value={ptCodeInput}
            onChange={(e) => setPtCodeInput(clean(e.target.value))}
            onBlur={() => {
              setPtCodeInput(normalizePtLoginCode(ptCodeInput));
            }}
            placeholder="e.g. P01, T01, PAR005_MARGARET, or TCH006_ANDREW"
            autoComplete="off"
          />

          <label className="pt-login-label">PIN</label>
          <input
            className="pt-login-input"
            value={pin}
            onChange={(e) => setPin(digitsOnly(e.target.value).slice(0, 6))}
            placeholder="6-digit PIN"
            inputMode="numeric"
            autoComplete="off"
          />

          <button className="pt-login-btn" type="submit" disabled={!canSubmit || loading}>
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <div className="pt-login-links">
          <NavLink to="/">← Home</NavLink>
          <NavLink to="/login">Admin/Doctor Login</NavLink>
        </div>

        <div className="pt-login-footer">© {year} Doctor Is In App. All rights reserved.</div>
      </div>
    </div>
  );
};

export default ParentTeacherLogin;